/**
 * RDV proposés par Otto — ce que le front en montre, sans rien re-dériver.
 *
 * La projection (fin de créneau, contact, compte, RDV déjà prévus) vient de la RPC
 * `get_appointment_proposals`, lecteur unique de la zone « Il faut que tu tranches » et
 * de la carte de mission (principe 11). Ce module ne fait que la mise en mots et la
 * conversion d'heure pour le champ de correction — dans le fuseau de l'entreprise (passé en
 * paramètre : `useOrganizationTimeZone()`, PLAN-GENERICITE § 4 quinquies étape 7), quel que
 * soit le fuseau du navigateur.
 */

export type ProposalKind = 'create' | 'reschedule' | 'cancel' | 'attendees';

export interface UpcomingAppointment {
  id: string;
  title: string | null;
  start_time: string;
}

/**
 * Ce qui occupe déjà le créneau d'une proposition (`appointment_slot_conflicts`, 07/10/2026) : un RDV
 * AutoQuote, un bloc d'Otto (option proposée à un autre client, save the date) ou un autre événement
 * de l'agenda Google. `id` est la clé de l'objet dans sa source (RDV, `calendar_holds`, événement).
 */
export type SlotConflictSource = 'appointment' | 'option' | 'save_the_date' | 'event';

export interface SlotConflict {
  source: SlotConflictSource;
  id: string;
  title: string | null;
  start_time: string;
  end_time: string;
  contact_name: string | null;
  account_name: string | null;
  /** La mission qui a posé le bloc (option, save the date). */
  mission_thread_id: string | null;
  /** Option : retirée seule à cette date sans choix du client. */
  expires_at: string | null;
}

/** Une ligne de `get_appointment_proposals`. */
export interface AppointmentProposal {
  id: string;
  kind: ProposalKind;
  status: 'pending' | 'executing' | 'approved' | 'rejected' | 'superseded';
  /** Validation interrompue (> 10 min en `executing`) : la proposition redevient décidable. */
  claim_stale: boolean;
  created_at: string;
  start_time: string | null;
  end_time: string | null;
  duration_minutes: number | null;
  appointment_type_id: string | null;
  appointment_type_name: string | null;
  custom_title: string | null;
  contact_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  account_id: string | null;
  account_name: string | null;
  /** Création : invités en plus du contact. Changement d'invités : adresses à ajouter. */
  additional_attendees: string[];
  /** Changement d'invités : adresses à retirer. */
  remove_attendees: string[];
  /** Invités actuels (hors contact) du RDV visé. */
  target_attendees: string[] | null;
  notes: string | null;
  cancel_reason: string | null;
  target_appointment_id: string | null;
  target_title: string | null;
  target_start_time: string | null;
  mission_thread_id: string | null;
  mission_objective: string | null;
  thread_key: string | null;
  other_upcoming: UpcomingAppointment[];
  last_error: string | null;
  decided_at: string | null;
  decision_note: string | null;
  booked_appointment_id: string | null;
  /** Création ou report vivants : ce qui occupe déjà le créneau proposé (tampons du type compris). */
  slot_conflicts: SlotConflict[];
}

export function proposalKindLabel(kind: ProposalKind): string {
  if (kind === 'create') return 'Rendez-vous proposé';
  if (kind === 'reschedule') return 'Report proposé';
  if (kind === 'attendees') return "Changement d'invités proposé";
  return 'Annulation proposée';
}

export function approveLabel(kind: ProposalKind): string {
  if (kind === 'create') return "Valider et envoyer l'invitation";
  if (kind === 'reschedule') return 'Valider le report';
  if (kind === 'attendees') return 'Valider le changement';
  return "Valider l'annulation";
}

const CONFLICT_KIND_LABELS: Record<SlotConflictSource, string> = {
  appointment: 'Rendez-vous',
  option: 'Option proposée',
  save_the_date: 'Save the date',
  event: 'Agenda Google',
};

/** « Option proposée · Thomas Docquier (Cars Denis) · 11:30–12:00 · retirée seule le jeu. 8 oct. » */
export function slotConflictDetail(conflict: SlotConflict, timeZone: string): string {
  const who = conflict.contact_name
    ? conflict.account_name ? `${conflict.contact_name} (${conflict.account_name})` : conflict.contact_name
    : conflict.account_name;
  const time = (iso: string) => new Intl.DateTimeFormat('fr-FR', { timeZone, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  const expiry = conflict.source === 'option' && conflict.expires_at
    ? `retirée seule le ${new Intl.DateTimeFormat('fr-FR', { timeZone, weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(conflict.expires_at))} sans réponse`
    : null;
  return [CONFLICT_KIND_LABELS[conflict.source], who, `${time(conflict.start_time)}–${time(conflict.end_time)}`, expiry]
    .filter(Boolean)
    .join(' · ');
}

/** Ce que le clic déclenche CHEZ LE CLIENT — dit avant le clic, pas découvert après. */
export function clientImpactText(kind: ProposalKind): string {
  if (kind === 'create') return "Le contact recevra l'invitation Google Calendar et le mail de confirmation.";
  if (kind === 'reschedule') return "L'événement sera déplacé dans l'agenda du contact.";
  if (kind === 'attendees') {
    return "Les invités ajoutés recevront l'invitation ; l'événement quittera l'agenda des invités retirés.";
  }
  return "L'événement sera retiré de l'agenda du contact, qui recevra un mail d'annulation.";
}

/** Ce qui change dans l'invitation, en clair : « Retirer x · Ajouter y ». */
export function attendeeChangeLine(p: Pick<AppointmentProposal, 'additional_attendees' | 'remove_attendees'>): string {
  const parts: string[] = [];
  if (p.remove_attendees.length > 0) parts.push(`Retirer ${p.remove_attendees.join(', ')}`);
  if (p.additional_attendees.length > 0) parts.push(`Ajouter ${p.additional_attendees.join(', ')}`);
  return parts.join(' · ');
}

/** Le titre lisible du rendez-vous : titre libre, sinon type, sinon titre du RDV visé. */
export function proposalTitle(p: Pick<AppointmentProposal, 'custom_title' | 'appointment_type_name' | 'target_title'>): string {
  return p.custom_title?.trim() || p.appointment_type_name || p.target_title || 'Rendez-vous';
}

export function contactLine(p: Pick<AppointmentProposal, 'contact_name' | 'contact_email' | 'account_name'>): string {
  const who = p.contact_name || p.contact_email || 'Contact inconnu';
  return p.account_name ? `${who} · ${p.account_name}` : who;
}

function zonedParts(date: Date, timeZone: string): Record<string, string> {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

/** « mer. 16 sept. · 10:00–11:00 » dans le fuseau donné. */
export function formatSlot(startIso: string | null, endIso: string | null, timeZone: string): string {
  if (!startIso) return 'Date à définir';
  const start = new Date(startIso);
  const day = new Intl.DateTimeFormat('fr-FR', { timeZone, weekday: 'short', day: 'numeric', month: 'short' })
    .format(start);
  const time = (d: Date) => new Intl.DateTimeFormat('fr-FR', { timeZone, hour: '2-digit', minute: '2-digit' }).format(d);
  return endIso ? `${day} · ${time(start)}–${time(new Date(endIso))}` : `${day} · ${time(start)}`;
}

/** ISO → valeur d'un `<input type="datetime-local">`, lue dans le fuseau donné. */
export function toZonedInputValue(iso: string | null, timeZone: string): string {
  if (!iso) return '';
  const p = zonedParts(new Date(iso), timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Décalage du fuseau (minutes, positif à l'est) à un instant donné — Paris : CET +60, CEST +120. */
function offsetMinutes(utcMs: number, timeZone: string): number {
  const p = zonedParts(new Date(utcMs), timeZone);
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute));
  return Math.round((asUtc - Math.floor(utcMs / 60000) * 60000) / 60000);
}

/**
 * Valeur d'un `<input type="datetime-local">` saisie comme heure du fuseau donné → ISO UTC.
 * Deux passes pour tomber juste de part et d'autre d'un changement d'heure.
 */
export function fromZonedInputValue(value: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number);
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi);
  const firstGuess = wallAsUtc - offsetMinutes(wallAsUtc, timeZone) * 60000;
  const utcMs = wallAsUtc - offsetMinutes(firstGuess, timeZone) * 60000;
  return new Date(utcMs).toISOString();
}
